import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';
import { useLabels } from 'wsm-common-data';
import { EllipsisCta } from 'wsm-ellipsis-cta';

import {
	stepsConfig,
	VEHICLE_TOOLBAR_BTN_PROPS,
	NAVIGATE_TO_CTA_DESTINATION,
	SCROLL_THE_PAGE_TO_UDS_SECTION
} from '../constants';
import useStepsListener from '../hooks/useStepsListener';
import useStepClickHandler from '../hooks/useStepClickHandler';
import useTrackEvent from '../hooks/useTrackEvent';
import PopoverContent from './PopoverContent';

const StepCtas = () => {
	const pubsub = isBrowser && window?.DDC?.pubsub;
	const [activeStep, setActiveStep] = useState('payment');
	const labels = useLabels();
	const steps = useSelector((state) => state.steps);
	const sendTrackEvent = useTrackEvent();
	const activeBtnLabel = labels.get(
		stepsConfig[activeStep].ctaLabel || stepsConfig[activeStep].titleLabel
	);

	const activeRoute = stepsConfig[activeStep].route;
	const isScrollToUDS = activeStep === 'payment';

	const onClickHandler = useStepClickHandler(
		activeRoute,
		activeBtnLabel,
		isScrollToUDS
	);

	const onVehicleToolbarClickHandler = useCallback(
		(event) => {
			// For the CTA that gets rendered in ws-vehicle-toolbar,
			// we pass `false` for the `shouldSendTrackEvent` parameter,
			// as the tracking event is sent in wsm-vehicle-cta-display instead
			onClickHandler(event, false);
		},
		[onClickHandler]
	);

	const onEllipsisClickHandler = useCallback(
		(mode) => {
			const isOpened = mode === 'opened';
			sendTrackEvent({
				element: isOpened ? 'CTA Skewer' : 'Close button',
				elememtCTA: undefined,
				result: isOpened
					? 'Expand alternative CTA menu'
					: 'Collapse alternative CTA menu'
			});
		},
		[sendTrackEvent]
	);

	// register SDP events to track steps progress
	useStepsListener();

	useEffect(() => {
		let nextStep;
		Object.keys(steps).some((step) => {
			if (!steps[step].complete && steps[step].enabled) {
				nextStep = step;
				return true;
			}
			return false;
		});
		setActiveStep(nextStep);
	}, [steps]);

	useEffect(() => {
		if (pubsub) {
			const vehicleToolbarBtnProps = {
				...VEHICLE_TOOLBAR_BTN_PROPS,
				btnLabel: activeBtnLabel,
				btnAttributes: {
					...VEHICLE_TOOLBAR_BTN_PROPS.btnAttributes,
					onClick: onVehicleToolbarClickHandler,
					// The tracking event is sent in wsm-vehicle-cta-display,
					// and the `element` property on the widget user event is defined there as well
					widgetUserEventData: {
						elementCTA: activeBtnLabel,
						result: isScrollToUDS
							? SCROLL_THE_PAGE_TO_UDS_SECTION
							: NAVIGATE_TO_CTA_DESTINATION
					}
				}
			};

			pubsub.publishDelayed('ws-vehicle-toolbar/show-vehicle-ctas', [
				vehicleToolbarBtnProps
			]);

			logger.info(
				`WS-TRACK-MY-OFFER -> ws-vehicle-toolbar/show-vehicle-ctas: ${JSON.stringify(
					vehicleToolbarBtnProps,
					null,
					2
				)}`
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeBtnLabel, onVehicleToolbarClickHandler]);

	return (
		<div className="d-flex flex-row my-4">
			<button
				type="button"
				className="btn btn-block btn-primary mr-2"
				aria-label={activeBtnLabel}
				onClick={onClickHandler}
			>
				{activeBtnLabel}
			</button>
			<EllipsisCta
				popoverContent={
					<PopoverContent activeStep={activeStep} steps={steps} />
				}
				onClick={onEllipsisClickHandler}
			/>
		</div>
	);
};

export default StepCtas;
